import { ChangeDetectorRef, Component, OnDestroy, OnInit, Input, Output, EventEmitter, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material';
import { UUID } from 'angular2-uuid';
import * as moment from 'moment';

import { AsperaService } from '../aspera/aspera.service';
import { Transfer } from './transfer.model';
import { TransferFile } from './transfer-file.model';
import { TransfersService } from './transfers.service';
import { FadeInAnimation } from '../shared/fade-in.animation';
import { ListItemAnimation } from '../shared/list-item.animation';
import { TimeOrDatePipe } from '../shared/time-or-date.pipe';
import { SuccessDialogComponent } from '../shared/success-dialog.component';
import { ErrorDialogComponent } from '../shared/error-dialog.component';
import { ExtendExpirationDialogComponent } from './extend-expiration-dialog.component';
import { AuthService } from '../auth/auth.service';
import { Subscription } from 'rxjs';

interface ActiveTransfer {
  files: TransferFile[];
  transfer: Transfer;
  cookie: string;
}

@Component({
  selector: 'app-transfer',
  templateUrl: './transfer.component.html',
  styleUrls: ['./transfer.component.scss'],
  animations: [ListItemAnimation(64), FadeInAnimation]
})
export class TransferComponent implements OnInit, OnDestroy {
  @Input() transfer: Transfer;
  @Output() transferStarted = new EventEmitter<ActiveTransfer>();

  activeFiles: TransferFile[];
  asperaRunning: boolean = this.asperaService.running;
  private statusSub: Subscription | null = null;
  get downloadAllText(): string { return this.asperaService.buttonLabel('Download All'); }
  get downloadText(): string { return this.asperaService.buttonLabel('Download'); }
  cookie: string;
  currentUser = this.authService.currentUser;
  expirationLoading: boolean = false;
  metadataVisible: boolean = false;
  signedIn: boolean = this.authService.signedIn;
  get metadataIds(): string[] {
    let fieldIds = []
    if (this.transfer != null && this.transfer.metadata != null) {
      for (let key in this.transfer.metadata) {
        fieldIds.push(key);
      }
    }
    return fieldIds;
  }

  constructor(
    private asperaService: AsperaService,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private router: Router,
    private transfersService: TransfersService,
    private authService: AuthService,
    private cd: ChangeDetectorRef) { }

  ngOnInit() {
    this.statusSub = this.asperaService.status$.subscribe(() => {
      this.ngZone.run(() => {
        this.asperaRunning = this.asperaService.running;
        this.cd.markForCheck();
      });
    });
  }

  ngOnDestroy() {
    if (this.statusSub != null) { this.statusSub.unsubscribe(); }
  }

  loadAspera(onSuccess?: () => void, onFailure?: () => void) {
    this.asperaService.startSession(() => {
      this.asperaRunning = this.asperaService.running;
      if (this.asperaRunning) {
        if (onSuccess != null) { onSuccess() }
      } else {
        if (onFailure != null) { onFailure() }
      }
    });
  }

  onDownloadFiles(files: TransferFile[]) {
    let download = () => this.downloadFiles(files);
    if (this.asperaRunning) {
      download();
    } else {
      this.loadAspera(
        () => download(),
        () => this.asperaService.showDesktopModal()
      )
    }
  }

  downloadFiles(files: TransferFile[]) {
    this.activeFiles = files.map((f) => {
      const file = new TransferFile();
      file.corrId = f.corrId;
      file.name = f.name;
      file.path = f.path;
      file.size = f.size;
      return file;
    });
    this.transfersService.transferFiles('download', this.transfer.uploadPackageGuid, this.transfer.uploadPackageId, files).subscribe(
      (response) => {
        const requestId = response.requestId;
        this.cookie = response.cookie;
        this.transferStarted.emit({
          cookie: this.cookie,
          files: this.activeFiles,
          transfer: this.transfer
        });
      },
      (errors) => {
        console.log('Transfer could not be started'); console.log(errors);
        this.onTransferEnd('failed');
      }
    )
  }

  onTransferEnd(status: string) {
    console.log("Transfer ended. Status: " + status);
    this.activeFiles = null;
    if (status === "completed") {
      this.ngZone.run(() => {this.showSuccessDialog("Files Downloaded")})
    } else {
      this.ngZone.run(() => {this.showErrorDialog("Download Failed")})
    }
  }

  showSuccessDialog(message: string) {
    let dialogRef = this.dialog.open(SuccessDialogComponent, {
      height: '240px',
      width: '320px',
      data: {message: message}
    });
    setTimeout(() => dialogRef.close(), 3000);
  }

  showErrorDialog(message: string) {
    let dialogRef = this.dialog.open(ErrorDialogComponent, {
      height: '240px',
      width: '320px',
      data: {message: message}
    });
    setTimeout(() => dialogRef.close(), 6000);
  }

  labelForField(fieldId) {
    switch (fieldId) {
    case "expiration_date":
      return "Original Expiration Date";
    default:
      return fieldId.replace('_',' ').toCapitalized()
    }
  }

  valueForField(fieldId: string): string {
    let value = this.transfer.metadata[fieldId];
    if (value != null) {
      return value.map((v) => v.displayed).join(', ');
    }
  }

  isRecipientsEmpty(): any {
    if (this.transfer.recipients.length < 1) {
      return false;
    }
    let recipients = this.transfer.recipients.map((r) => r.name || r.email).join(", ");
    if (recipients.trim() === "") {
      return false;
    } else {
      return true;
    }
  }

  recipientNames(): any {
    let recipients = this.transfer.recipients.map((r) => r.name || r.email).join(", ");
    if (recipients) {
      return recipients;
    } else {
      return false;
    }
  }

  landingPadsNames(): any {
    if (this.transfer.metadata.Destinations != undefined && this.transfer.metadata.Destinations != null) {
      return this.transfer.metadata.Destinations.map((d) => d.displayed).join(", ");
    } else {
      return "";
    }
  }

  jellyRollNames(): any {
    if (this.transfer.metadata.jellyroll != undefined && this.transfer.metadata.jellyroll != null) {
      return this.transfer.metadata.jellyroll.map((d) => d.displayed).join(", ");
    } else {
      return "";
    }
  }

  toggleMetadata() {
    this.metadataVisible = !this.metadataVisible;
  }

  showExtendExpirationDialog(expiration: string) {
    let dialogRef = this.dialog.open(ExtendExpirationDialogComponent, {
      height: '240px',
      width: '320px',
      data: {expiration: expiration}
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result != null && result.expiration != null) {
        this.extendExpiration(result.expiration);
      }
    })
  }

  extendExpiration(expiration: string) {
    this.expirationLoading = true;
    let expirationMoment = moment(expiration, 'YYYY-MM-DD')
    if (expirationMoment.isValid()) {
      this.transfersService.extendExpiration(this.transfer, expiration).subscribe(
        (success) => {
          this.transfer.expirationDate = expirationMoment;
          this.showSuccessDialog("Expiration Successfully Extended");
          this.expirationLoading = false;
        },
        (error) => {
          this.showErrorDialog("Failed to Extend Expiration");
          this.expirationLoading = false;
        }
      )
    } else {
      this.showErrorDialog("Failed to Extend Expiration");
      this.expirationLoading = false;
    }
  }

  sendAnother() {
    let query = {p: this.transfer.uploadPackageGuid};
    this.router.navigate(["/home"], {queryParams: query});
  }
}



// WEBPACK FOOTER //
// ./src/app/transfers/transfer.component.ts